// src/pages/seeker/SeekerExperience.jsx

import React, { useEffect, useState, useRef } from 'react';
import { seekerAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { X, Check, Loader, Copy, Trash2, MoreVertical, Briefcase, MapPin, Clock, Pencil, ListChecks, Sparkles, ChevronUp, ChevronDown } from 'lucide-react';
import { FieldError, useConfirm, SkillExtractModal, AddBtn, DelBtn, EditBtn, usePersistedState } from '../../components/shared/UI';

const C = {
  navy: '#0e4e95', white: '#ffffff',
  offwhite: '#f4f7fb', text: '#1a1a1a', muted: '#666',
  border: '#d0daea', blue: '#3D80CC',
};

const FL = ({ label, required, children, hint, half }) => (
  <div style={{ marginBottom: '14px', width: half ? 'calc(50% - 6px)' : '100%' }}>
    <label className="form-label">{label}{required && <span style={{ color: '#CC0000', marginLeft: '2px' }}>*</span>}</label>
    {children}
    {hint && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '3px' }}>{hint}</div>}
  </div>
);

// ── Seniority ──────────────────────────────────────────
const SENIORITY_LEVELS = [
  { value: 'UNSPECIFIED',       label: 'Unspecified' },
  { value: 'VOLUNTEER',         label: 'Volunteer' },
  { value: 'OFFICER',           label: 'Officer' },
  { value: 'SUPERVISOR',        label: 'Supervisor' },
  { value: 'ASSISTANT_MANAGER', label: 'Assistant Manager' },
  { value: 'MANAGER',           label: 'Manager' },
  { value: 'SENIOR_MANAGER',    label: 'Senior Manager' },
  { value: 'EXECUTIVE_MANAGER', label: 'Executive Manager+' },
];

const SENIORITY_LABEL = Object.fromEntries(SENIORITY_LEVELS.map(l => [l.value, l.label]));

const suggestSeniority = (positionName) => {
  if (!positionName) return null;
  const lower = positionName.toLowerCase();
  if (/\b(executive\s+director|executive\s+manager|chief|ceo|cto|coo|cfo|president|vp\b|vice[\s-]*president|managing\s+director|partner\b|founder\b)\b/.test(lower)) return 'EXECUTIVE_MANAGER';
  if (/\b(senior\s+manager|general\s+manager|\bdirector\b|head\s+of|department\s+head)\b/.test(lower)) return 'SENIOR_MANAGER';
  if (/\b(assistant\s+manager|deputy\s+manager|\bdeputy\b)\b/.test(lower)) return 'ASSISTANT_MANAGER';
  if (/\bmanager\b/.test(lower)) return 'MANAGER';
  if (/\b(supervisor|\bteam\s+lead\b|team\s+leader|\blead\b|foreman)\b/.test(lower)) return 'SUPERVISOR';
  if (/\bvolunteer\b/.test(lower)) return 'VOLUNTEER';
  if (/\b(officer|analyst|coordinator|administrator|associate|specialist|consultant|adviser|advisor|developer|engineer|accountant|technician|representative|\bclerk\b|\bassistant\b)\b/.test(lower)) return 'OFFICER';
  return null;
};

// ── Helpers ────────────────────────────────────────────
const calcDurationMonths = (startDate, endDate) => {
  if (!startDate || !endDate) return '';
  const norm = (d) => d.length === 4 ? d + '-01' : d;
  const s = new Date(norm(startDate));
  const e = new Date(norm(endDate));
  const months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
  return months > 0 ? String(months) : '';
};

const calcEndDate = (dateStarted, durationMonths) => {
  if (!dateStarted || !durationMonths || parseInt(durationMonths) <= 0) return null;
  const d = new Date(dateStarted + '-01');
  d.setMonth(d.getMonth() + parseInt(durationMonths));
  return d.toISOString();
};

const formatDuration = (months) => {
  const m = parseInt(months, 10);
  if (!m || m <= 0) return null;
  const yrs = Math.floor(m / 12);
  const rem = m % 12;
  const parts = [];
  if (yrs > 0) parts.push(`${yrs} year${yrs !== 1 ? 's' : ''}`);
  if (rem > 0) parts.push(`${rem} month${rem !== 1 ? 's' : ''}`);
  return parts.join(' ');
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
};

const toISODate = (d) => {
  if (!d) return null;
  if (d.length === 4) return new Date(d + '-01-01T00:00:00').toISOString();
  if (d.length === 7) return new Date(d + '-01T00:00:00').toISOString();
  return new Date(d).toISOString();
};

// Convert API record → form shape
const expFromApi = (e) => ({
  id:             e.id,
  employer:       e.company    || '',
  position:       e.jobTitle   || '',
  location:       e.location   || '',
  schedule:       e.schedule   || '',
  adminLevel:     e.adminLevel || '',
  dateStarted:    e.startDate ? (e.startDate.length === 4 ? e.startDate : e.startDate.slice(0, 7)) : '',
  durationMonths: e.isCurrent ? '' : calcDurationMonths(e.startDate, e.endDate),
  roles:          Array.isArray(e.roles) ? e.roles : [],
});

// Convert form shape → API payload
const expToApi = (form) => ({
  company:    form.employer.trim(),
  jobTitle:   form.position.trim(),
  location:   form.location?.trim() || null,
  schedule:   form.schedule   || null,
  adminLevel: form.adminLevel || null,
  startDate: toISODate(form.dateStarted),
  endDate:   calcEndDate(form.dateStarted, form.durationMonths),
  isCurrent: !form.durationMonths || parseInt(form.durationMonths) <= 0,
  roles:     form.roles?.length
    ? form.roles.map(r => ({
        id:          r.id,
        title:       r.title?.trim()       || '',
        description: r.description?.trim() || null,
        tasks:       (r.tasks || [])
          .filter(t => t.text?.trim())
          .map(t => ({ id: t.id, text: t.text.trim() })),
      }))
    : null,
});

const emptyExp = () => ({
  id:             crypto.randomUUID(),
  employer:       '',
  position:       '',
  location:       '',
  schedule:       '',
  adminLevel:     '',
  dateStarted:    '',
  durationMonths: '',
  roles:          [],
  _isNew:         true,
});

const emptyRole = () => ({
  id:          crypto.randomUUID(),
  title:       '',
  description: '',
  tasks:       [],
});

const emptyTask = () => ({ id: crypto.randomUUID(), text: '' });

// ── Core Roles Editor ──────────────────────────────────
const CoreRolesEditor = ({ roles, onChange }) => {
  const taskRefs = useRef({});
  const pendingFocusId = useRef(null);

  useEffect(() => {
    if (pendingFocusId.current && taskRefs.current[pendingFocusId.current]) {
      taskRefs.current[pendingFocusId.current].focus();
      pendingFocusId.current = null;
    }
  });

  const addRole = () => onChange([...roles, emptyRole()]);

  const removeRole = (rid) => onChange(roles.filter(r => r.id !== rid));

  const updateRole = (rid, field, val) =>
    onChange(roles.map(r => r.id === rid ? { ...r, [field]: val } : r));

  const addTask = (rid) => {
    const newTask = emptyTask();
    pendingFocusId.current = newTask.id;
    onChange(roles.map(r => r.id === rid ? { ...r, tasks: [...r.tasks, newTask] } : r));
  };

  const removeTask = (rid, tid) =>
    onChange(roles.map(r => r.id === rid ? { ...r, tasks: r.tasks.filter(t => t.id !== tid) } : r));

  const updateTask = (rid, tid, val) =>
    onChange(roles.map(r => r.id === rid
      ? { ...r, tasks: r.tasks.map(t => t.id === tid ? { ...t, text: val } : t) }
      : r));

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <label className="form-label" style={{ marginBottom: 0 }}>Core Roles</label>
        <AddBtn onClick={addRole} size={11} title="Add Role" />
      </div>

      {roles.map((role, idx) => (
        <div key={role.id} style={{
          background: '#f8fafd', border: `1px solid ${C.border}`,
          borderRadius: 6, padding: '12px 14px', marginBottom: 10,
        }}>
          {/* Role title row */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <input
              className="form-input"
              value={role.title}
              onChange={e => updateRole(role.id, 'title', e.target.value)}
              placeholder={`Core Role ${idx + 1} — e.g. Backend Developer`}
              style={{ flex: 1 }}
            />
            <DelBtn onClick={() => removeRole(role.id)} size={13} />
          </div>

          {/* Tasks */}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
              Main Tasks
            </label>
            {role.tasks.map((task, tidx) => (
              <div key={task.id} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: C.muted, flexShrink: 0, width: 16, textAlign: 'right' }}>
                  {tidx + 1}.
                </span>
                <input
                  ref={el => { if (el) taskRefs.current[task.id] = el; else delete taskRefs.current[task.id]; }}
                  className="form-input"
                  value={task.text}
                  onChange={e => updateTask(role.id, task.id, e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTask(role.id); } }}
                  placeholder="Describe a main task…"
                  style={{ flex: 1, fontSize: 12 }}
                />
                <DelBtn onClick={() => removeTask(role.id, task.id)} size={12} />
              </div>
            ))}
            <AddBtn onClick={() => addTask(role.id)} size={11} title="Add Task" />
          </div>
        </div>
      ))}

      {roles.length === 0 && (
        <div style={{ fontSize: 12, color: C.muted, fontStyle: 'italic', marginTop: 2 }}>
          No core roles added yet. Click "Add Role" to define the roles associated with this position.
        </div>
      )}
    </div>
  );
};

// ── Card menu ──────────────────────────────────────────
const CardMenu = ({ onEdit, onDuplicate, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const Item = ({ onClick, icon, label, danger }) => (
    <button
      onClick={() => { onClick(); setOpen(false); }}
      style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '9px 14px', fontSize: '13px', background: 'none', border: 'none', cursor: 'pointer', color: danger ? 'var(--danger)' : 'var(--text)', borderBottom: danger ? 'none' : `1px solid ${C.border}` }}
      onMouseEnter={e => e.currentTarget.style.background = danger ? '#fff5f5' : C.offwhite}
      onMouseLeave={e => e.currentTarget.style.background = 'none'}
    >
      {icon} {label}
    </button>
  );

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.8)', display: 'flex', padding: '4px', borderRadius: '3px', flexShrink: 0 }}
        onMouseEnter={e => e.currentTarget.style.color = '#fff'}
        onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.8)'}
      >
        <MoreVertical size={16} />
      </button>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 9999, background: C.white, border: `1px solid ${C.border}`, borderRadius: '4px', boxShadow: '0 4px 16px rgba(0,0,0,0.18)', minWidth: '140px', overflow: 'hidden' }}>
          <Item onClick={onEdit}      icon={<Pencil size={13} />} label="Edit" />
          <Item onClick={onDuplicate} icon={<Copy size={13} />}   label="Duplicate" />
          <Item onClick={onDelete}    icon={<Trash2 size={13} />} label="Delete" danger />
        </div>
      )}
    </div>
  );
};

// ── Display mode ───────────────────────────────────────
const ExpDisplay = ({ exp, onEdit, onDuplicate, onDelete, onExtract, isExtracting, onUpdateRoles }) => {
  const duration = formatDuration(exp.durationMonths);
  const started  = formatDate(exp.dateStarted);
  const roles    = exp.roles || [];

  const [open, setOpen] = usePersistedState(`tikhet:seeker:exp:${exp.id}:open`, true);
  const [editingRoles, setEditingRoles] = useState(false);
  const [draftRoles,   setDraftRoles]   = useState(() => (exp.roles || []).map(r => ({ ...r, tasks: (r.tasks || []).map(t => ({ ...t })) })));
  const [savingRoles,  setSavingRoles]  = useState(false);

  const startEditingRoles = (withExtra = false) => {
    const base = (exp.roles || []).map(r => ({ ...r, tasks: (r.tasks || []).map(t => ({ ...t })) }));
    setDraftRoles(withExtra ? [...base, emptyRole()] : base);
    setEditingRoles(true);
  };

  const cancelEditingRoles = () => {
    setDraftRoles((exp.roles || []).map(r => ({ ...r, tasks: (r.tasks || []).map(t => ({ ...t })) })));
    setEditingRoles(false);
  };

  const saveEditingRoles = async () => {
    setSavingRoles(true);
    try {
      await onUpdateRoles(draftRoles);
      setEditingRoles(false);
    } finally { setSavingRoles(false); }
  };

  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '6px', marginBottom: '14px', overflow: 'visible' }}>
      <div
        style={{ padding: '8px 12px', background: '#4A6FA5', borderRadius: open ? '6px 6px 0 0' : '6px', display: 'flex', alignItems: 'center', gap: '10px', position: 'relative', cursor: 'pointer', userSelect: 'none' }}
        onClick={(e) => {
          if (e.target.closest('button, [data-cardmenu]')) return;
          setOpen(o => !o);
        }}
      >
        <span style={{ color: '#E8EDF2', fontSize: '14px', fontWeight: 800, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '8px' }}>
          {exp.position || <span style={{ fontStyle: 'italic', opacity: 0.6, fontWeight: 400, textTransform: 'none' }}>Untitled position</span>}
          {exp.adminLevel && exp.adminLevel !== 'UNSPECIFIED' && (
            <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', background: 'rgba(255,255,255,0.2)', color: '#E8EDF2', border: '1px solid rgba(255,255,255,0.3)', flexShrink: 0, textTransform: 'none', letterSpacing: 'normal' }}>
              {SENIORITY_LABEL[exp.adminLevel]}
            </span>
          )}
        </span>
        <CardMenu onEdit={onEdit} onDuplicate={onDuplicate} onDelete={onDelete} />
        {open ? <ChevronUp size={14} color="#E8EDF2" style={{ opacity: 0.85, flexShrink: 0 }} /> : <ChevronDown size={14} color="#E8EDF2" style={{ opacity: 0.85, flexShrink: 0 }} />}
      </div>

      {open && <>
      <div style={{ padding: '12px 16px 14px' }}>
        {/* Basic info */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: roles.length ? 14 : 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Briefcase size={15} color={C.muted} style={{ flexShrink: 0 }} />
            <span style={{ fontSize: '15px', fontWeight: 600, color: C.text }}>
              {exp.employer || <span style={{ color: C.muted, fontStyle: 'italic' }}>No employer</span>}
            </span>
            {exp.schedule && (
              <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', background: '#e8f0fb', color: C.blue, border: `1px solid #c5d8f5`, flexShrink: 0 }}>
                {exp.schedule}
              </span>
            )}
          </div>
          {exp.location && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <MapPin size={15} color={C.muted} style={{ flexShrink: 0 }} />
              <span style={{ fontSize: '14px', color: C.text }}>{exp.location}</span>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Clock size={15} color={C.muted} style={{ flexShrink: 0 }} />
            <span style={{ fontSize: '14px', color: C.text }}>
              {started ? `Started ${started}` : 'Start date not set'}
              {duration
                ? <span style={{ color: C.muted }}> · {duration}</span>
                : exp.dateStarted ? <span style={{ color: C.muted }}> · Current</span> : null}
            </span>
          </div>
        </div>

        {/* Core Roles */}
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <ListChecks size={13} color={C.blue} />
            <span style={{ fontSize: 11, fontWeight: 700, color: C.blue, textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1 }}>
              Core Roles
            </span>
            {!editingRoles && (
              <>
                {roles.length > 0 && (
                  <EditBtn onClick={() => startEditingRoles(false)} size={12} title="Edit roles" />
                )}
                <AddBtn onClick={() => startEditingRoles(true)} size={12} title="Add role" />
              </>
            )}
          </div>

          {editingRoles ? (
            <>
              <CoreRolesEditor roles={draftRoles} onChange={setDraftRoles} />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
                <button onClick={cancelEditingRoles} disabled={savingRoles} className="btn btn-ghost btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <X size={12} /> Cancel
                </button>
                <button onClick={saveEditingRoles} disabled={savingRoles} className="btn btn-primary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  {savingRoles ? <><Loader size={12} /> Saving…</> : <><Check size={12} /> Save Roles</>}
                </button>
              </div>
            </>
          ) : roles.length > 0 ? (
            roles.map((role) => (
              <div key={role.id} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>
                  {role.title || <span style={{ color: C.muted, fontStyle: 'italic' }}>Untitled role</span>}
                </div>
                {role.tasks?.length > 0 && (
                  <ul style={{ margin: 0, paddingLeft: 18, listStyleType: 'disc' }}>
                    {role.tasks.map((task) => (
                      <li key={task.id} style={{ fontSize: 12, color: C.text, marginBottom: 3, lineHeight: 1.5 }}>
                        {task.text}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))
          ) : (
            <div style={{ fontSize: 12, color: C.muted, fontStyle: 'italic' }}>
              No core roles added yet. Use the + button above to define roles and tasks.
            </div>
          )}
        </div>
      </div>

      <div style={{ borderTop: `1px solid ${C.border}`, padding: '7px 16px', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={onExtract}
          disabled={isExtracting}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: isExtracting ? C.muted : C.blue, background: 'none', border: 'none', cursor: isExtracting ? 'default' : 'pointer', padding: '2px 0', fontFamily: "'Exo 2', sans-serif" }}
        >
          {isExtracting
            ? <><Loader size={12} style={{ animation: 'spin 0.8s linear infinite' }} /> Extracting…</>
            : <><Sparkles size={12} /> Extract Skills</>}
        </button>
      </div>
      </>}
    </div>
  );
};

// ── Edit mode ──────────────────────────────────────────
const ExpEdit = ({ exp, onSave, onCancel }) => {
  const [form,   setForm]   = useState({ ...exp });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [seniorityHint, setSeniorityHint] = useState(null);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target ? e.target.value : e }));

  const handleSave = async () => {
    const errs = {};
    if (!form.position.trim())  errs.position    = 'Position name is required';
    if (!form.employer.trim())  errs.employer    = 'Employer name is required';
    if (!form.dateStarted)      errs.dateStarted = 'Start date is required';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setSaving(true);
    try {
      await onSave({ ...form, _isNew: form._isNew });
    } finally { setSaving(false); }
  };

  return (
    <div style={{ background: C.white, border: `2px solid ${C.blue}`, borderRadius: '6px', marginBottom: '14px', overflow: 'visible' }}>
      <div style={{ background: '#f0f6ff', padding: '10px 14px', borderBottom: `1px solid ${C.border}`, borderRadius: '6px 6px 0 0' }}>
        <span style={{ fontSize: '13px', fontWeight: 700, color: C.blue, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Editing Experience</span>
      </div>

      <div style={{ padding: '16px' }}>
        <FL label="Position Name" required>
          <input
            value={form.position}
            onChange={e => {
              set('position')(e);
              setErrors(er => ({ ...er, position: '' }));
              const suggested = suggestSeniority(e.target.value);
              setSeniorityHint(suggested && suggested !== form.adminLevel ? suggested : null);
            }}
            placeholder="e.g. Senior Accountant"
            className="form-input"
          />
          {errors.position && <FieldError>{errors.position}</FieldError>}
        </FL>

        <FL label="Admin Level">
          <select
            value={form.adminLevel}
            onChange={e => { setForm(f => ({ ...f, adminLevel: e.target.value })); setSeniorityHint(null); }}
            className="form-input"
            style={{ background: 'white' }}
          >
            <option value="">— select —</option>
            {SENIORITY_LEVELS.map(l => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </select>
          {seniorityHint && (
            <div style={{ fontSize: '11px', color: C.blue, marginTop: '3px' }}>
              Suggested: <button
                type="button"
                onClick={() => { setForm(f => ({ ...f, adminLevel: seniorityHint })); setSeniorityHint(null); }}
                style={{ background: 'none', border: 'none', padding: 0, color: C.blue, fontWeight: 700, cursor: 'pointer', fontSize: '11px', textDecoration: 'underline', fontFamily: 'inherit' }}
              >
                {SENIORITY_LABEL[seniorityHint]}
              </button>
            </div>
          )}
        </FL>

        <FL label="Employer Name" required>
          <input value={form.employer} onChange={e => { set('employer')(e); setErrors(er => ({ ...er, employer: '' })); }} placeholder="e.g. Deloitte" className="form-input" />
          {errors.employer && <FieldError>{errors.employer}</FieldError>}
        </FL>

        <FL label="Schedule">
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', paddingTop: '4px' }}>
            {['Full-time', 'Part-time', 'Casual', 'Contract'].map(opt => (
              <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: C.text, cursor: 'pointer', fontWeight: 400 }}>
                <input
                  type="radio"
                  name={`schedule-${form.id}`}
                  value={opt}
                  checked={form.schedule === opt}
                  onChange={() => setForm(f => ({ ...f, schedule: opt }))}
                  onClick={() => { if (form.schedule === opt) setForm(f => ({ ...f, schedule: '' })); }}
                  style={{ accentColor: C.blue, cursor: 'pointer', width: '15px', height: '15px' }}
                />
                {opt}
              </label>
            ))}
          </div>
        </FL>

          <FL label="Date Started" required>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="number"
                value={form.dateStarted ? form.dateStarted.slice(0, 4) : ''}
                onChange={e => {
                  const y = e.target.value;
                  const m = form.dateStarted && form.dateStarted.length >= 7 ? form.dateStarted.slice(5, 7) : '';
                  set('dateStarted')({ target: { value: y ? (m ? `${y}-${m}` : y) : '' } });
                  setErrors(er => ({ ...er, dateStarted: '' }));
                }}
                placeholder="YYYY"
                min="1950"
                max="2099"
                className="form-input"
                style={{ width: '80px' }}
              />
              <select
                value={form.dateStarted && form.dateStarted.length >= 7 ? form.dateStarted.slice(5, 7) : ''}
                onChange={e => {
                  const m = e.target.value;
                  const y = form.dateStarted ? form.dateStarted.slice(0, 4) : '';
                  set('dateStarted')({ target: { value: y ? (m ? `${y}-${m}` : y) : '' } });
                }}
                className="form-input"
                style={{ flex: 1 }}
              >
                <option value="">Month (optional)</option>
                {['January','February','March','April','May','June','July','August','September','October','November','December'].map((name, i) => (
                  <option key={i} value={String(i + 1).padStart(2, '0')}>{name}</option>
                ))}
              </select>
            </div>
            {errors.dateStarted && <FieldError>{errors.dateStarted}</FieldError>}
          </FL>
          <FL label="Duration (months)" hint={formatDuration(form.durationMonths) ? `= ${formatDuration(form.durationMonths)}` : 'e.g. 18 for 1 year 6 months'}>
            <input
              type="number"
              min="1"
              max="600"
              value={form.durationMonths}
              onChange={set('durationMonths')}
              className="form-input"
            />
          </FL>

        <FL label="Location">
          <input value={form.location} onChange={set('location')} placeholder="e.g. Sydney, NSW" className="form-input" />
        </FL>

        <CoreRolesEditor
          roles={form.roles}
          onChange={roles => setForm(f => ({ ...f, roles }))}
        />
      </div>

      <div style={{ padding: '10px 16px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: '8px', background: C.offwhite, borderRadius: '0 0 4px 4px' }}>
        <button onClick={onCancel} disabled={saving} className="btn btn-danger" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <X size={13} /> Cancel
        </button>
        <button onClick={handleSave} disabled={saving} className="btn btn-success" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          {saving ? <><Loader size={13} /> Saving…</> : <><Check size={13} /> Save</>}
        </button>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════
export const SeekerExperience = () => {
  const { confirm, dialog } = useConfirm();
  const [experiences,  setExperiences]  = useState([]);
  const [editing,      setEditing]      = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [extracting,   setExtracting]   = useState(null); // ID of card being extracted
  const [extractModal, setExtractModal] = useState(null); // { sourceLabel, skills, profileSkillNames }
  const [skillCache,   setSkillCache]   = useState({});   // { [expId]: string[] } — cleared on save

  const load = async () => {
    try {
      const res = await seekerAPI.getExperience();
      setExperiences((res.data.data || []).map(expFromApi));
    } catch { toast.error('Failed to load experience'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSaveCard = async (updated) => {
    const payload = expToApi(updated);
    try {
      if (updated._isNew) {
        await seekerAPI.addExperience(payload);
      } else {
        await seekerAPI.updateExperience(updated.id, payload);
        setSkillCache(prev => { const next = { ...prev }; delete next[updated.id]; return next; });
      }
      await load();
      setEditing(null);
      toast.success('Work experience saved');
    } catch {
      toast.error('Failed to save experience');
    }
  };

  const handleUpdateRoles = async (id, newRoles) => {
    const current = experiences.find(e => e.id === id);
    if (!current) return;
    const updated = { ...current, roles: newRoles };
    await seekerAPI.updateExperience(id, expToApi(updated));
    setSkillCache(prev => { const next = { ...prev }; delete next[id]; return next; });
    await load();
    toast.success('Roles saved');
  };

  const handleCancelCard = (id) => {
    const e = experiences.find(e => e.id === id);
    if (e?._isNew) setExperiences(prev => prev.filter(e => e.id !== id));
    setEditing(null);
  };

  const handleDuplicate = async (id) => {
    const e = experiences.find(e => e.id === id);
    if (!e) return;
    try {
      await seekerAPI.addExperience(expToApi(e));
      await load();
      toast.success('Work experience duplicated');
    } catch { toast.error('Failed to duplicate'); }
  };

  const handleDelete = async (id) => {
    if (!await confirm('Delete Experience', 'Are you sure you want to delete this work experience entry?')) return;
    try {
      await seekerAPI.deleteExperience(id);
      setExperiences(prev => prev.filter(e => e.id !== id));
      if (editing === id) setEditing(null);
      toast.success('Work experience deleted');
    } catch { toast.error('Failed to delete experience'); }
  };

  const handleAddNew = () => {
    const e = emptyExp();
    setExperiences(prev => [...prev, e]);
    setEditing(e.id);
  };

  const handleExtract = async (exp) => {
    setExtracting(exp.id);

    // Phase 1: check for existing links from this source
    let existingLinkCount = 0;
    try {
      const derivationsRes = await seekerAPI.getSkillDerivations();
      existingLinkCount = (derivationsRes.data.data?.experienceLinks || [])
        .filter(l => l.workExperienceId === exp.id).length;
    } catch { setExtracting(null); return; }

    // Warn and clear if re-extracting
    let cacheCleared = false;
    if (existingLinkCount > 0) {
      setExtracting(null);
      const ok = await confirm(
        'Re-extract Skills?',
        `This entry already has ${existingLinkCount} skill association${existingLinkCount !== 1 ? 's' : ''} recorded.\n\nRe-extracting will remove all of them. Skills with no other source will also be removed from your profile.\n\nThis cannot be undone.`,
        { confirmLabel: 'Yes, Re-extract', cancelLabel: 'Cancel' }
      );
      if (!ok) return;
      setExtracting(exp.id);
      try {
        const clearRes = await seekerAPI.clearSourceLinks({ sourceType: 'experience', sourceId: exp.id });
        const { skillsRemoved } = clearRes.data.data || {};
        if (skillsRemoved > 0) toast.success(`${skillsRemoved} skill${skillsRemoved !== 1 ? 's' : ''} removed from your profile`);
      } catch { setExtracting(null); return; }
      setSkillCache(prev => { const next = { ...prev }; delete next[exp.id]; return next; });
      cacheCleared = true;
    }

    // Phase 2: extract and open modal
    try {
      const hasCached = !cacheCleared && (exp.id in skillCache);
      const [profileRes, extractRes] = await Promise.all([
        seekerAPI.getProfile(),
        hasCached ? Promise.resolve(null) : seekerAPI.extractSkills({ sourceType: 'experience', sourceId: exp.id }),
      ]);
      const skills = hasCached ? skillCache[exp.id] : (extractRes.data.data?.skills || []);
      if (!hasCached) setSkillCache(prev => ({ ...prev, [exp.id]: skills }));

      const profileSkillMap = {};
      for (const jss of profileRes.data.data?.skills || []) {
        profileSkillMap[jss.skill.displayName.toLowerCase()] = jss.id;
      }

      setExtractModal({
        sourceId: exp.id,
        sourceType: 'experience',
        sourceLabel: [exp.position, exp.employer].filter(Boolean).join(' at '),
        skills,
        alreadyLinkedSkillNames: [], // always empty here — either first extraction or just cleared
        profileSkillMap,
      });
    } catch { /* interceptor toasts */ }
    finally { setExtracting(null); }
  };

  const handleAddExtractedSkills = async (skillNames) => {
    const { sourceId, sourceType, profileSkillMap } = extractModal;
    let addedCount = 0;
    for (const name of skillNames) {
      let jobSeekerSkillId;
      try {
        const res = await seekerAPI.addSkill({ displayName: name });
        jobSeekerSkillId = res.data.data.id;
        addedCount++;
      } catch {
        jobSeekerSkillId = profileSkillMap[name.toLowerCase()];
      }
      if (jobSeekerSkillId) {
        try {
          if (sourceType === 'education') {
            await seekerAPI.addEducationLink({ jobSeekerSkillId, educationId: sourceId, sourceType: 'EXTRACTED' });
          } else {
            await seekerAPI.addExperienceLink({ jobSeekerSkillId, workExperienceId: sourceId, sourceType: 'EXTRACTED' });
          }
        } catch { /* upsert handles duplicates; silently ignore other errors */ }
      }
    }
    if (addedCount > 0) toast.success(`${addedCount} skill${addedCount !== 1 ? 's' : ''} added to your profile`);
    setExtractModal(null);
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>;

  return (
    <div className="fade-in">
      {dialog}
      <SkillExtractModal
        open={!!extractModal}
        onClose={() => setExtractModal(null)}
        sourceLabel={extractModal?.sourceLabel}
        suggestedSkills={extractModal?.skills || []}
        alreadyLinkedSkillNames={extractModal?.alreadyLinkedSkillNames || []}
        onAdd={handleAddExtractedSkills}
      />
      <div className="page-header">
        <h1>Experience</h1>
        <p>Your work history — used across all your CVs</p>
      </div>

      <div className="page-content">
        <div style={{ marginBottom: '16px' }}>

          {experiences.length === 0 && (
            <div style={{ textAlign: 'center', padding: '32px 0', color: C.muted, background: C.white, border: `1px solid ${C.border}`, borderRadius: '6px', marginBottom: '14px' }}>
              <Briefcase size={36} color={C.border} style={{ marginBottom: '10px' }} />
              <p style={{ fontSize: '13px', margin: 0 }}>No experience added yet</p>
            </div>
          )}

          {experiences.map(e =>
            editing === e.id ? (
              <ExpEdit
                key={e.id}
                exp={e}
                onSave={handleSaveCard}
                onCancel={() => handleCancelCard(e.id)}
              />
            ) : (
              <ExpDisplay
                key={e.id}
                exp={e}
                onEdit={() => setEditing(e.id)}
                onDuplicate={() => handleDuplicate(e.id)}
                onDelete={() => handleDelete(e.id)}
                onExtract={() => handleExtract(e)}
                isExtracting={extracting === e.id}
                onUpdateRoles={(newRoles) => handleUpdateRoles(e.id, newRoles)}
              />
            )
          )}

          <AddBtn onClick={handleAddNew} size={15} title="Add Work Experience" />
        </div>
      </div>
    </div>
  );
};
